import { useEffect, useState } from 'react';
import { apiFetch } from '../auth/apiFetch';
import { pollWithWake } from './kioskRefresh';

// Net-worth snapshots from /api/net-worth/history, oldest first as the API
// returns them. Shared by the dashboard's history chart and the home screen's
// Finance sparkline.

export interface NetWorthSnapshot {
  date: string; // YYYY-MM-DD
  source: 'daily' | 'backfill';
  household: number;
  dakota: number;
  natalia: number;
}

interface HistoryResponse {
  snapshots: NetWorthSnapshot[];
}

const REFRESH_MS = 60 * 60 * 1000;

export function useNetWorthHistory(): {
  snapshots: NetWorthSnapshot[] | null;
  error: string | null;
} {
  const [snapshots, setSnapshots] = useState<NetWorthSnapshot[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let loaded = false;
    // Hourly, and again whenever the app comes back to the foreground — a
    // phone keeps the home screen mounted for days, and a new snapshot lands
    // each morning. A failed refresh keeps the last good series on screen
    // (and doesn't surface an error, which would replace the chart).
    const load = async () => {
      try {
        const res = await apiFetch('/api/net-worth/history');
        if (!res.ok) throw new Error(`history ${res.status}`);
        const body = (await res.json()) as HistoryResponse;
        // Neon returns DATE as a full ISO timestamp via JSON. Trim to
        // YYYY-MM-DD here so every downstream consumer (anchor, sort,
        // axis label, date parsing) sees the same compact format.
        const normalized = body.snapshots.map((s) => ({
          ...s,
          date: typeof s.date === 'string' ? s.date.slice(0, 10) : s.date,
        }));
        if (!cancelled) {
          loaded = true;
          setSnapshots(normalized);
          setError(null);
        }
      } catch (err) {
        if (!cancelled && !loaded) {
          setError(err instanceof Error ? err.message : 'failed to load history');
        }
      }
    };
    void load();
    const stop = pollWithWake(() => void load(), REFRESH_MS);
    return () => {
      cancelled = true;
      stop();
    };
  }, []);

  return { snapshots, error };
}

// One value per calendar day — where a daily and a backfill row share a date
// (the cron caught up after a revision) the daily row wins, as it's the
// canonical going-forward source. Sorted oldest first.
export function dedupeSnapshotsByDate(snapshots: NetWorthSnapshot[]): NetWorthSnapshot[] {
  const byDate = new Map<string, NetWorthSnapshot>();
  for (const s of snapshots) {
    const prev = byDate.get(s.date);
    if (!prev || (s.source === 'daily' && prev.source !== 'daily')) {
      byDate.set(s.date, s);
    }
  }
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
}
